import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { Platform, PopoverController } from "@ionic/angular";
import { Store } from "@ngrx/store";
import moment from "moment";
import { IMqttMessage, MqttService } from "ngx-mqtt";
import { Subscription } from "rxjs";
import { filter, tap } from "rxjs/operators";
import { Gateway } from "src/components/shared-components/class/general-classes/gateway";
import { GatewayKindGroup } from "src/model/class/general classes/gateway-kind-group";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { GenericStoreApiData } from "src/components/shared-components/interfaces/generic-store-api-data";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iMqttHandling } from "src/model/interface/iMqtt-handling";
import { EventService } from "src/service/event.service";
import { GatewayService } from "src/service/gateway.service";
import { LoggerService } from "src/service/logger.service";
import { MqttClientService } from "src/service/mqtt-client.service";
import { TranslationService } from "src/service/translation.service";
import { UtilityService } from "src/service/utility.service";
import { listGateway } from "src/state/selectors/gateway.selectors";
import { PopoverObjectInfoComponent } from "../popover-object-info/popover-object-info.component";

@Component({
    selector: "app-footer",
    templateUrl: "./footer.component.html",
    styleUrls: ["./footer.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FooterComponent
  implements OnInit, OnDestroy, iEvent, iMqttHandling {
  private _logContext: string = "Footer.component";
  private _subscriptionGatewayList: Subscription;

  _mqttAddressSubscriptionsMap: Map<string, Subscription> = null;

  public _eventSubscriptions: Array<Subscription> = null;

  public isDesktop: boolean = false;
  public gatewayKindList: Array<GatewayKindGroup>;
  public lastTelegramReceived: string = "";

  // DISMISSED
  private popoverOpened: boolean = false;

  constructor(
    private _gatewayService: GatewayService,
    private _platform: Platform,
    private _eventService: EventService,
    private _popoverCtrl: PopoverController,
    private _mqttService: MqttClientService,
    private _translationService: TranslationService,
    private _loggerService: LoggerService,
    private _mqttServiceNative: MqttService,
    private _utilityService: UtilityService,
    private _store: Store
  ) {
    this.gatewayKindList = [];
  }

  ngOnInit() {
    this._mqttAddressSubscriptionsMap = new Map<string, Subscription>();

    this._platform.ready().then((readySource) => {
      this.isDesktop = !(
        this._platform.is("ios") || this._platform.is("android")
      );
    });

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "ngOnInit",
      "IN ngOnInit"
    );
    this._gatewayService.Initialize();

    this.subscribeEvents();

    this.subscribeGatewaysService();

    this.subscribeMQTTAddresses();
  }

  async ngOnDestroy() {
    if (this._subscriptionGatewayList) {
      this._subscriptionGatewayList.unsubscribe();
    }

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "ngOnDestroy",
      "LOCAL MAP-SUBSCRIPTIONS 1: "
    );
    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "ngOnDestroy",
        "LOCAL MAP-SUBSCRIPTIONS 1 - ENTRY: " + entry[0]
      );
    }


    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "ngOnDestroy",
      "MQTT native observables ARRAY 1: "
    );
    for (const entry of Array.from(
      new Map(Object.entries(this._mqttServiceNative.observables)).entries()
    )) {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "ngOnDestroy",
        "MQTT native observables ARRAY 1 - ENTRY: " + entry[0]
      );
    }

    await this.unsubscribeToMQTTAddresses();

    this.unsubscribeEvents();

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "ngOnDestroy",
      "LOCAL MAP-SUBSCRIPTIONS 2: "
    );
    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "ngOnDestroy",
        "LOCAL MAP-SUBSCRIPTIONS 2 - ENTRY: " + entry[0]
      );
    }

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "ngOnDestroy",
      "MQTT native observables ARRAY 2: "
    );
    for (const entry of Array.from(
      new Map(Object.entries(this._mqttServiceNative.observables)).entries()
    )) {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "ngOnDestroy",
        "MQTT native observables ARRAY 2 - ENTRY: " + entry[0]
      );
    }
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    /**React to log-out */
    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(() => {
        this.gatewayKindList = [];
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  // 2020-11-16: GF: list of topics that we want to hide on status bar
  private isTelegramVisibleOnStatusBar(topic: string, payloadmessage) {
    return !(
      topic.includes("ACK_NACK") ||
      topic.includes("RESERVED") ||
      payloadmessage.Action
    );
  }

  // GF: used to show the popover with the detail of the gateways
  public async onClickChip(event: any, group: GatewayKindGroup) {
    this.popoverOpened = true;
    const popover = await this._popoverCtrl.create({
      component: PopoverObjectInfoComponent,
      componentProps: {
        objGatewayGroup: group,
      },
      cssClass: "popover-side-top",
      event: event,
      translucent: true,
      animated: true,
      showBackdrop: false,
    });

    await popover.present();

    popover.onWillDismiss().then(() => {
      this.popoverOpened = false;
      group.visibleName = "";
    });
  }

  // DISMISSED: GF: used at first to show the name of the gateway only when the mouse is hover
  chipHover(isHover: boolean, group: GatewayKindGroup) {
    if (!this.popoverOpened) {
      if (isHover) group.visibleName = group.gatewayKindName();
      else group.visibleName = "";
    }
  }

  private subscribeGatewaysService(): void {
    // 2020-11-12 GF: when gateways list changes the dot changes too
    this._subscriptionGatewayList = this._store
      .select(listGateway)
      .pipe(
        filter(
          (listGatewayObject: GenericStoreApiData) => listGatewayObject !== null && listGatewayObject !== undefined
        ),
        filter(
          (listGatewayObject: GenericStoreApiData) => listGatewayObject?.loaded
        ),
        filter(
          (listGatewayStore: GenericStoreApiData) =>
            !(
              listGatewayStore.errorGeneric ||
              listGatewayStore.errorNotYetImplemented
            )
        ),
        tap((listGatewayStore: GenericStoreApiData) => {
          if (listGatewayStore.isOldDataInUse) {
            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              "_store.select(listGateway)",
              "subscribeGatewaysService()",
              "We are using old data!!"
            );
          }
        })
      )
      .subscribe((listGatewayObject: GenericStoreApiData) => {
        if (!listGatewayObject.errorGeneric) {

          const gatewayList = listGatewayObject.data?.data || [];

          const gatewayTypedList: Gateway[] = gatewayList.map(gateway => new Gateway(gateway));

          this.fillGatewayKindList(gatewayTypedList);
        }
      });
  }

  /**
   * Fill gateway kind group item
   *
   * @param listGateway Full list of gateway
   */
  private fillGatewayKindList(listGateway: Array<Gateway>): void {
    for (const gateway of listGateway) {
      if (gateway.isVisible()) {
        const indexCurrentGatewayKindGroup: number =
          this.gatewayKindList.findIndex(
            (gatewayKindList) => gatewayKindList.kind === gateway.kind
          );

        if (indexCurrentGatewayKindGroup < 0) {
          const newGroup = new GatewayKindGroup(gateway.kind);
          newGroup.listGateways.push(gateway);
          newGroup.gatewayKindGroupStatus();
          newGroup.gatewayKindGroupActive();
          newGroup.failedBusOperationCount();
          this.gatewayKindList.push(newGroup);
        } else {
          // otherwise add/update the gateway inside the group
          this.gatewayKindList[indexCurrentGatewayKindGroup].addGateway(
            gateway
          );
        }
      }
    }
  }

  //#region MQTT-address handling

  async subscribeMQTTAddresses() {
    let mqttAddress: string = "";
    let mqttAddressSubcription: Subscription = null;
    //let returnedObservable: Observable<IMqttMessage> = null;

    // 2020-16-11:GF: when telegram is received check if we have to show it, if yes add it to status bar

    mqttAddress = "#";

    const returnedObservable = await this._mqttService.subscribeTopic(
      mqttAddress
    );

    if (returnedObservable && returnedObservable != null) {
      mqttAddressSubcription = returnedObservable.subscribe(
        (message: IMqttMessage) => {
          const payloadMessage = JSON.parse(message.payload.toString()).Message;

          if (
            this.isTelegramVisibleOnStatusBar(message.topic, payloadMessage)
          ) {
            const instanceguid_address = this._utilityService.AddressSplit(
              message.topic
            );
            if (instanceguid_address.length == 2) {
              this.lastTelegramReceived =
                instanceguid_address[1] +
                (!payloadMessage.AddressName
                  ? ""
                  : " - " + payloadMessage.AddressName) +
                " - " +
                payloadMessage.State +
                " | " +
                (!payloadMessage.TimeStamp
                  ? ""
                  : "" +
                  this._translationService.formatDateTime(
                    moment(payloadMessage.TimeStamp, "YYYYMMDDHHmmss")
                  ) +
                  "");
            }
          }
        }
      );
      await this.checkLocalAddressSubscriptionMap(
        mqttAddress,
        mqttAddressSubcription
      );
    }
  }

  async checkLocalAddressSubscriptionMap(
    addressToCheck: string,
    addressSubscription: Subscription
  ): Promise<boolean> {
    let result: boolean = false;
    if (this._mqttAddressSubscriptionsMap.has(addressToCheck)) {
      addressSubscription.unsubscribe();

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "Address ALREADY present " + addressToCheck
      );
    } else {
      this._mqttAddressSubscriptionsMap.set(
        addressToCheck,
        addressSubscription
      );

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "NEW address: " + addressToCheck
      );
      result = true;
    }
    return result;
  }

  async unsubscribeToMQTTAddresses(): Promise<void> {
    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      entry[1].unsubscribe();

      this._mqttAddressSubscriptionsMap.delete(entry[0]);
    }
  }

  //#endregion
}
